import {
  Box,
  Typography,
  Grid,
  Button,
  FormHelperText,
  Chip,
  Paper,
  Skeleton,
  Fade,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from "@mui/material";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { AddOnCard, AmenityCard } from "../index";
import Iconify from "components/Iconify";

const EmptyState = ({ title, subtitle }) => (
  <Box
    sx={{
      minHeight: 140,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      border: "1px dashed #d2d6da",
      borderRadius: 2,
      backgroundColor: "#f9fafb",
      px: 2,
      py: 3,
    }}
  >
    <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
      {title}
    </Typography>
    <Typography variant="caption" sx={{ color: "#7b809a", maxWidth: 520 }}>
      {subtitle}
    </Typography>
  </Box>
);

export const StepAmenities = ({
  values,
  priceKey,
  loadingAmenities,
  availableAmenities,
  touched,
  errors,
  canStep1,
  setFieldValue,
  setActiveStep,
  loadingAddOns,
  availableAddons, // flat global list
}) => {
  const [expandedDates, setExpandedDates] = useState([]);

  useEffect(() => {
    if (availableAmenities?.length) {
      setExpandedDates([availableAmenities[0].date]);
    }
  }, [availableAmenities]);

  const handleAccordionChange = (date) => (_e, isExpanded) => {
    setExpandedDates((prev) => (isExpanded ? [...prev, date] : prev.filter((d) => d !== date)));
  };

  const handleAmenityToggle = (date, id) => {
    const curr = values?.selectedAmenities?.[date] || [];
    const isRemoving = curr.includes(id);
    const updated = isRemoving ? curr.filter((x) => x !== id) : [...curr, id];

    const updatedAmenities = { ...values?.selectedAmenities, [date]: updated };

    if (isRemoving && updated.length === 0) {
      const updatedAddOns = { ...values?.selectedAddOns, [date]: [] };
      setFieldValue("selectedAddOns", updatedAddOns);
    }

    setFieldValue("selectedAmenities", updatedAmenities);
  };

  const handleAddOnsToggle = (date, id) => {
    const hasAmenitySelected = (values?.selectedAmenities?.[date] || []).length > 0;
    if (!hasAmenitySelected) return; // blocked — no amenity chosen for this date yet

    const curr = values?.selectedAddOns?.[date] || [];
    const updated = curr.includes(id) ? curr.filter((x) => x !== id) : [id];
    setFieldValue("selectedAddOns", { ...values?.selectedAddOns, [date]: updated });
  };

  return (
    <Fade in timeout={350}>
      <Box>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            p: { xs: 2.5, md: 3.5 },
            border: `1px solid #dee2e6`,
            backgroundColor: "#ffffff",
          }}
        >
          <Box display="flex" alignItems="center" gap={1.5} mb={2.5}>
            <Box flex={1}>
              <Typography variant="h6" color="#344767">
                Available Amenities
              </Typography>
              <Typography variant="caption" sx={{ color: "#7b809a" }}>
                Select amenities and add-ons separately for each date
              </Typography>
            </Box>
          </Box>

          {loadingAmenities ? (
            <Grid container spacing={2}>
              {[1, 2].map((i) => (
                <Grid item xs={12} key={i}>
                  <Skeleton variant="rounded" height={80} />
                </Grid>
              ))}
            </Grid>
          ) : availableAmenities?.length > 0 ? (
            availableAmenities.map(({ date, amenities }) => {
              const selectedAmenityIds = values?.selectedAmenities?.[date] || [];
              const selectedAddOnIds = values?.selectedAddOns?.[date] || [];
              const selectedCount = selectedAmenityIds.length + selectedAddOnIds.length;
              const hasAmenitySelected = selectedAmenityIds.length > 0;

              return (
                <Accordion
                  key={date}
                  expanded={expandedDates.includes(date)}
                  onChange={handleAccordionChange(date)}
                  disableGutters
                  elevation={0}
                  sx={{
                    border: "1px solid #dee2e6",
                    borderRadius: "12px !important",
                    mb: 1.5,
                    overflow: "hidden",
                    "&::before": { display: "none" },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<Iconify icon="mdi:chevron-down" />}
                    sx={{
                      backgroundColor: selectedCount > 0 ? "#FFF5F5" : "#fafafa",
                      "& .MuiAccordionSummary-content": { alignItems: "center" },
                    }}
                  >
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      width="100%"
                      pr={1}
                    >
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <Iconify icon="mdi:calendar-month-outline" sx={{ color: "#A68181" }} />
                        <Typography fontWeight={700} fontSize={15} color="#344767">
                          {dayjs(date).format("DD MMM YYYY, dddd")}
                        </Typography>
                      </Box>
                      {selectedCount > 0 && (
                        <Chip
                          label={`${selectedCount} selected`}
                          size="small"
                          sx={{
                            backgroundColor: "#A6818118",
                            color: "#9A3B3B",
                            border: "1px solid #A6818144",
                          }}
                        />
                      )}
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails sx={{ backgroundColor: "#ffffff", pt: 2 }}>
                    <Typography variant="subtitle2" fontWeight={700} color="#344767" mb={1.5}>
                      Amenities
                    </Typography>

                    <Grid container spacing={1.5} alignItems="stretch">
                      {amenities?.length > 0 ? (
                        amenities.map((item) => (
                          <Grid item xs={12} sm={6} key={item.id}>
                            <AmenityCard
                              amenity={item}
                              selected={selectedAmenityIds.includes(item?.id)}
                              onToggle={(id) => handleAmenityToggle(date, id)}
                              disabled={false}
                              priceKey={priceKey}
                              values={values}
                            />
                          </Grid>
                        ))
                      ) : (
                        <Grid item xs={12}>
                          <EmptyState
                            title="No Amenities Available"
                            subtitle="There are no amenities available for this date. Try choosing a different slot or date."
                          />
                        </Grid>
                      )}
                    </Grid>

                    <Divider sx={{ my: 2.5 }} />

                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                      <Typography variant="subtitle2" fontWeight={700} color="#344767">
                        Add-Ons
                      </Typography>
                      {!hasAmenitySelected && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#9A3B3B",
                            backgroundColor: "#FFF2F2",
                            border: "1px solid #f3d4d4",
                            borderRadius: 1.5,
                            px: 1,
                            py: 0.25,
                            fontSize: 11,
                          }}
                        >
                          Select an amenity first to unlock add-ons
                        </Typography>
                      )}
                    </Box>

                    {loadingAddOns ? (
                      <Grid container spacing={2}>
                        {[1, 2].map((i) => (
                          <Grid item xs={12} sm={6} key={i}>
                            <Skeleton variant="rounded" height={90} />
                          </Grid>
                        ))}
                      </Grid>
                    ) : (
                      <Grid
                        container
                        spacing={1.5}
                        alignItems="stretch"
                        sx={{
                          opacity: hasAmenitySelected ? 1 : 0.5,
                          pointerEvents: hasAmenitySelected ? "auto" : "none",
                          transition: "opacity 0.2s",
                        }}
                      >
                        {availableAddons?.length > 0 ? (
                          availableAddons.map((item) => (
                            <Grid item xs={12} sm={6} key={item?.id}>
                              <AddOnCard
                                amenity={item}
                                selected={selectedAddOnIds.includes(item?.id)}
                                onToggle={(id) => handleAddOnsToggle(date, id)}
                                disabled={!hasAmenitySelected}
                                priceKey={priceKey}
                                values={values}
                              />
                            </Grid>
                          ))
                        ) : (
                          <Grid item xs={12}>
                            <EmptyState
                              title="No Add-Ons Available"
                              subtitle="There are no add-ons available right now."
                            />
                          </Grid>
                        )}
                      </Grid>
                    )}
                  </AccordionDetails>
                </Accordion>
              );
            })
          ) : (
            <EmptyState
              title="No Amenities Available"
              subtitle="There are no amenities available for the selected date range and slot. Try choosing a different slot or date."
            />
          )}

          {touched?.selectedAmenities && errors?.selectedAmenities && (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <FormHelperText error sx={{ color: "#F44335" }}>
                {errors?.selectedAmenities}
              </FormHelperText>
            </Box>
          )}

          <Box mt={3} display="flex" justifyContent="space-between">
            <Button
              variant="outlined"
              onClick={() => {
                setFieldValue("selectedAmenities", {});
                setFieldValue("selectedAddOns", {});
                setActiveStep(0);
              }}
              sx={{
                borderRadius: 8,
                textTransform: "none",
                fontSize: 14,
                fontWeight: 600,
                borderColor: "#d2d6da",
                color: "#7b809a",
                "&:hover": { borderColor: "#A68181", color: "#9A3B3B", backgroundColor: "#FFF2F2" },
              }}
            >
              <Iconify sx={{ mr: 1 }} icon="mdi:arrow-left" /> Back
            </Button>
          </Box>
        </Paper>
      </Box>
    </Fade>
  );
};
